import type {UseQueryOptions} from '@tanstack/react-query'
import {keepPreviousData, queryOptions, useQuery} from '@tanstack/react-query'
import Big from 'big.js'

import type {SupportedLocales} from '@/lib/locale/locale'
import type {Order} from '@/models/orderModel'
import {getOrders} from '@/services/orderService'
import {NO_REFETCH_OPTIONS} from '@/utils/query/constants'

export function getOrdersQueryKey(params: {locale: SupportedLocales}) {
  return ['orders', params.locale] as const
}

export function getOrdersQueryOptions<TData = Order[], TError = Error>(
  params: {
    locale: SupportedLocales
  },
  options?: Omit<UseQueryOptions<Order[], TError, TData>, 'queryKey' | 'queryFn'>,
) {
  return queryOptions({
    queryKey: getOrdersQueryKey(params),
    queryFn: async ({signal}) => getOrders({signal, locale: params.locale}),
    placeholderData: keepPreviousData,
    ...NO_REFETCH_OPTIONS,
    ...options,
  })
}

const selectOrdersWithValue = (data: Order[]) => {
  return data.map(order => ({
    value: order.lineItems.reduce(
      (acc, item) => acc.add(Big(item.price).times(item.quantity)),
      Big(0),
    ),
    ...order,
  }))
}

export default function useOrders(locale: SupportedLocales) {
  return useQuery(getOrdersQueryOptions({locale}, {select: selectOrdersWithValue}))
}
